import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import TenantOccupantForm from "./TenantOccupantForm";
import Modal from "../Components/Modal";
import TenantOccupantCard from "./TenantOccupantCard";
import PageModal from "../Components/PageModal";
import TenantOccupantDetails from "./TenantOccupantDetails";
import {
  getDocDataSnap,
  setDocData,
  storage,
} from "../../../utils/firebase/Firebase.utils";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { motion, AnimatePresence } from "framer-motion";
import {
  IoChevronDownOutline,
  IoPersonAddOutline,
  IoSearchOutline,
  IoClose,
  IoDocumentTextOutline,
  IoInformationCircleOutline,
  IoImage,
  IoEyeOutline,
  IoPencilOutline,
  IoSaveOutline,
  IoCloseCircleOutline,
  IoAddCircleOutline,
  IoTrashOutline,
  IoCloudUploadOutline,
  IoDocumentAttachOutline,
} from "react-icons/io5";
import {
  FaRegUser,
  FaHome,
  FaIdCard,
  FaPhoneAlt,
  FaCalendarAlt,
  FaVenusMars,
  FaBirthdayCake,
  FaUserTie,
  FaFileAlt,
  FaFileUpload,
  FaFileDownload,
} from "react-icons/fa";
import OccupantFormData from "./OccupantFormData";
import OccupantDocuments from "./OccupantDocuments";
import NoPhoto from "../../assets/blankphoto.webp";

export default function OccupantsInformationFullPage({ tenant }) {
  const { id } = useParams();
  const [addMode, setAddMode] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [occupants, setOccupants] = useState([]);
  const [viewMode, setViewMode] = useState(false);
  const [editingOccupantId, setEditingOccupantId] = useState(null);
  const [selectedOccupant, setSelectedOccupant] = useState({});
  const [occupantIndex, setOccupantIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState({});
  const [editFormData, setEditFormData] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [documentType, setDocumentType] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [documentDescription, setDocumentDescription] = useState("");
  const [showAddDocForm, setShowAddDocForm] = useState(false);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoUploadProgress, setPhotoUploadProgress] = useState(0);

  useEffect(() => {
    const close = (e) => {
      if (e.keyCode === 27) {
        setViewMode(false);
      }
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, []);

  useEffect(() => {
    const fetchOccupants = async () => {
      getDocDataSnap("tenants", id, (tenant) => {
        const occupantsList = tenant.occupantsName || [];
        setOccupants(occupantsList);

        // Initialize active tabs for each occupant
        const tabs = {};
        occupantsList.forEach((occ) => {
          tabs[occ.id] = "Information";
        });
        setActiveTab(tabs);

        setIsLoading(false);
      });
    };
    fetchOccupants();
  }, [id]);

  const handleAddOccupant = () => {
    setAddMode(!addMode);
  };

  const handleViewOccupant = (occupant) => {
    setViewMode(!viewMode);
    setSelectedOccupant(occupant);
    setOccupantIndex(occupants.indexOf(occupant));
  };

  const handleEditOccupant = (occupant) => {
    setEditingOccupantId(occupant.id);
    setEditFormData({ ...occupant });
  };

  const handleCancelEdit = () => {
    setEditingOccupantId(null);
    setEditFormData({});
  };

  const handleSaveEdit = async () => {
    setIsProcessing(true);
    try {
      // Find the occupant index in the occupants array
      const updatedOccupants = [...occupants];
      const occupantIndex = updatedOccupants.findIndex(
        (occ) => occ.id === editingOccupantId
      );

      if (occupantIndex !== -1) {
        updatedOccupants[occupantIndex] = { ...editFormData };

        // Update the document in Firebase
        await setDocData("tenants", id, {
          occupantsName: updatedOccupants,
        });

        // Update local state
        setOccupants(updatedOccupants);
      }

      setEditingOccupantId(null);
      setEditFormData({});
    } catch (error) {
      console.error("Error updating occupant:", error);
    }
    setIsProcessing(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleAccordion = (occupantId) => {
    setExpandedId(expandedId === occupantId ? null : occupantId);
    // If we're closing an accordion that's in edit mode, cancel the edit
    if (expandedId === occupantId && editingOccupantId === occupantId) {
      handleCancelEdit();
    }
    // Reset the document form when closing
    if (expandedId === occupantId) {
      setShowAddDocForm(false);
    }
  };

  const handleTabChange = (occupantId, tab) => {
    setActiveTab((prev) => ({
      ...prev,
      [occupantId]: tab,
    }));
    // Reset the document form when changing tabs
    setShowAddDocForm(false);
  };

  const toggleAddDocForm = () => {
    setShowAddDocForm(!showAddDocForm);
    // Reset form fields
    setDocumentType("");
    setDocumentName("");
    setDocumentDescription("");
    setUploadProgress(0);
  };

  const handleDocumentUpload = async (e, occupantId) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!documentType || !documentName) {
      alert("Please provide document type and name");
      return;
    }

    setUploadingDoc(true);
    setUploadProgress(0);

    // Create a safe filename
    const timestamp = new Date().getTime();
    const safeFileName = file.name.replace(/[^a-zA-Z0-9.]/g, "_");
    const storagePath = `tenants/${id}/occupants/${occupantId}/documents/${timestamp}_${safeFileName}`;
    const storageRef = ref(storage, storagePath);

    // Create uploadTask
    const uploadTask = uploadBytesResumable(storageRef, file);

    // Listen for state changes
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        // Track progress
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setUploadProgress(progress);
      },
      (error) => {
        // Handle error
        console.error("Upload error:", error);
        setUploadingDoc(false);
        alert("Error uploading document. Please try again.");
      },
      async () => {
        // Upload completed successfully
        try {
          // Get download URL
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          // Prepare document object
          const newDocument = {
            id: `doc_${timestamp}`,
            name: documentName,
            type: documentType,
            description: documentDescription || "",
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            uploadDate: new Date().toISOString(),
            url: downloadURL,
            path: storagePath,
          };

          // Update occupant's documents in Firestore
          const updatedOccupants = [...occupants];
          const occupantIndex = updatedOccupants.findIndex(
            (occ) => occ.id === occupantId
          );

          if (occupantIndex !== -1) {
            const currentOccupant = updatedOccupants[occupantIndex];

            // Initialize documents array if it doesn't exist
            if (!currentOccupant.documents) {
              currentOccupant.documents = [];
            }

            // Add new document
            currentOccupant.documents.push(newDocument);

            // Update Firestore
            await setDocData("tenants", id, {
              occupantsName: updatedOccupants,
            });

            // Update local state
            setOccupants(updatedOccupants);
          }

          // Reset form
          setDocumentType("");
          setDocumentName("");
          setDocumentDescription("");
          setShowAddDocForm(false);
        } catch (error) {
          console.error("Error saving document metadata:", error);
          alert("Error saving document information. Please try again.");
        }

        setUploadingDoc(false);
      }
    );
  };

  const handleDeleteDocument = async (occupantId, documentId) => {
    // Use window.confirm instead of global confirm to avoid ESLint warning
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    setIsProcessing(true);

    try {
      // Find the occupant and document
      const updatedOccupants = [...occupants];
      const occupantIndex = updatedOccupants.findIndex(
        (occ) => occ.id === occupantId
      );

      if (occupantIndex !== -1) {
        const currentOccupant = updatedOccupants[occupantIndex];

        if (currentOccupant.documents) {
          // Find and remove the document
          const docIndex = currentOccupant.documents.findIndex(
            (doc) => doc.id === documentId
          );

          if (docIndex !== -1) {
            // Delete from array
            currentOccupant.documents.splice(docIndex, 1);

            // Update Firestore
            await setDocData("tenants", id, {
              occupantsName: updatedOccupants,
            });

            // Update local state
            setOccupants(updatedOccupants);
          }
        }
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      alert("Error deleting document. Please try again.");
    }

    setIsProcessing(false);
  };

  const getDocumentIcon = (fileType) => {
    if (fileType.includes("image")) {
      return <IoImage className="w-4 h-4 text-blue-500" />;
    } else if (fileType.includes("pdf")) {
      return <FaFileAlt className="w-4 h-4 text-red-500" />;
    } else {
      return <IoDocumentTextOutline className="w-4 h-4 text-gray-500" />;
    }
  };

  const filteredOccupants = occupants.filter((occupant) => {
    const fullName = `${occupant.firstName || ""} ${
      occupant.middleName || ""
    } ${occupant.lastName || ""}`.toLowerCase();
    return (
      fullName.includes(searchTerm.toLowerCase()) ||
      (occupant.relationship &&
        occupant.relationship.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  const handlePhotoUpload = async (e, occupantId) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.includes("image")) {
      alert("Please select an image file");
      return;
    }

    setUploadingPhoto(true);
    setPhotoUploadProgress(0);

    try {
      // Create a safe filename
      const timestamp = new Date().getTime();
      const safeFileName = file.name.replace(/[^a-zA-Z0-9.]/g, "_");
      const storagePath = `tenants/${id}/occupants/${occupantId}/photos/${timestamp}_${safeFileName}`;
      const storageRef = ref(storage, storagePath);

      // Create uploadTask
      const uploadTask = uploadBytesResumable(storageRef, file);

      // Listen for state changes
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          // Track progress
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setPhotoUploadProgress(progress);
        },
        (error) => {
          // Handle error
          console.error("Photo upload error:", error);
          setUploadingPhoto(false);
          alert("Error uploading photo. Please try again.");
        },
        async () => {
          // Upload completed successfully
          try {
            // Get download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            // Update occupant's photo in Firestore
            const updatedOccupants = [...occupants];
            const occupantIndex = updatedOccupants.findIndex(
              (occ) => occ.id === occupantId
            );

            if (occupantIndex !== -1) {
              // Update the photo URL
              updatedOccupants[occupantIndex].primaryPhoto = downloadURL;

              // Update Firestore
              await setDocData("tenants", id, {
                occupantsName: updatedOccupants,
              });

              // Update local state
              setOccupants(updatedOccupants);

              // Also update form data if editing
              if (editingOccupantId === occupantId) {
                setEditFormData((prev) => ({
                  ...prev,
                  primaryPhoto: downloadURL,
                }));
              }
            }
          } catch (error) {
            console.error("Error saving photo metadata:", error);
            alert("Error saving photo information. Please try again.");
          }

          setUploadingPhoto(false);
        }
      );
    } catch (error) {
      console.error("Error in photo upload process:", error);
      setUploadingPhoto(false);
      alert("Error processing photo: " + error.message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#363062]"></div>
      </div>
    );
  }

  const renderEditableField = (label, name, value, icon) => (
    <div className="flex justify-between items-center">
      <span className="text-xs text-gray-500 flex items-center">
        {icon &&
          React.cloneElement(icon, {
            className: "w-2.5 h-2.5 mr-1 text-gray-400",
          })}
        {label}
      </span>
      <input
        type="text"
        name={name}
        value={value || ""}
        onChange={handleInputChange}
        className="text-xs font-medium w-1/2 border border-gray-200 rounded-md p-1 focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062]"
      />
    </div>
  );

  const renderProfile = (occupant) => {
    if (editingOccupantId === occupant.id) {
      return (
        <div className="flex flex-col items-center justify-center p-3 border-b border-gray-100">
          <div className="relative mb-2">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-[#363062]/10 flex items-center justify-center mb-1">
              {editFormData.primaryPhoto ? (
                <img
                  src={editFormData.primaryPhoto}
                  alt={`${editFormData.firstName} ${editFormData.lastName}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <FaRegUser className="w-6 h-6 text-[#363062]" />
              )}
            </div>
            <button
              onClick={() => photoInputRef.current.click()}
              disabled={uploadingPhoto}
              className="absolute -bottom-1 -right-1 bg-[#363062] text-white p-1 rounded-full hover:bg-[#363062]/90"
            >
              <IoPencilOutline className="w-3 h-3" />
            </button>
            <input
              type="file"
              ref={photoInputRef}
              className="hidden"
              onChange={(e) => handlePhotoUpload(e, occupant.id)}
              accept="image/*"
            />
          </div>
          {uploadingPhoto && (
            <div className="w-full mt-1 mb-2">
              <div className="w-full bg-gray-200 rounded-full h-1">
                <div
                  className="bg-[#8FCD00] h-1 rounded-full"
                  style={{ width: `${photoUploadProgress}%` }}
                ></div>
              </div>
              <p className="text-[10px] text-gray-500 text-center mt-0.5">
                {photoUploadProgress}%
              </p>
            </div>
          )}
          <div className="text-center">
            <input
              type="text"
              name="firstName"
              value={editFormData.firstName || ""}
              onChange={handleInputChange}
              className="text-sm font-medium text-center border-0 border-b border-gray-200 focus:ring-0 focus:border-[#363062] px-1 py-0.5 w-full"
              placeholder="First Name"
            />
            <div className="flex space-x-1 justify-center">
              <input
                type="text"
                name="middleName"
                value={editFormData.middleName || ""}
                onChange={handleInputChange}
                className="text-xs text-gray-600 text-center border-0 border-b border-gray-100 focus:ring-0 focus:border-[#363062] px-1 py-0.5 w-20"
                placeholder="Middle"
              />
              <input
                type="text"
                name="lastName"
                value={editFormData.lastName || ""}
                onChange={handleInputChange}
                className="text-xs text-gray-600 text-center border-0 border-b border-gray-100 focus:ring-0 focus:border-[#363062] px-1 py-0.5 w-24"
                placeholder="Last Name"
              />
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="text-gray-600 text-left">
      {/* Header Section */}
      <div className="bg-white p-5 rounded-lg shadow-sm mb-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-medium text-[#363062]">
              Occupants Information
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Manage tenant's household members and guests
            </p>
          </div>
          <button
            onClick={handleAddOccupant}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-[#8FCD00] rounded-md hover:bg-[#8FCD00]/90 transition-colors duration-200 shadow-sm"
          >
            <IoPersonAddOutline className="w-3.5 h-3.5 mr-1.5" />
            Add Occupant
          </button>
        </div>

        {/* Search Bar */}
        <div className="mt-4 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <IoSearchOutline className="h-3.5 w-3.5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-9 pr-3 py-1.5 border border-gray-200 rounded-md focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062] placeholder-gray-400 text-xs"
            placeholder="Search occupants by name or relationship..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Occupants Accordion List */}
      <div className="space-y-3">
        {filteredOccupants.map((occupant, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100"
          >
            <button
              onClick={() => toggleAccordion(occupant.id)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full overflow-hidden bg-[#363062]/10 flex items-center justify-center">
                  {occupant.primaryPhoto ? (
                    <img
                      src={occupant.primaryPhoto}
                      alt={occupant.firstName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FaRegUser className="w-3.5 h-3.5 text-[#363062]" />
                  )}
                </div>
                <div className="text-left">
                  <h3 className="text-xs font-medium text-gray-900">
                    {occupant.firstName} {occupant.middleName}{" "}
                    {occupant.lastName}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {occupant.relationship || "Relationship not specified"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-[#363062]/10 text-[#363062] px-2 py-0.5 rounded-full text-[10px]">
                  {occupant.type || "Resident"}
                </span>
                <motion.div
                  animate={{ rotate: expandedId === occupant.id ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <IoChevronDownOutline className="w-4 h-4 text-gray-400" />
                </motion.div>
              </div>
            </button>

            <AnimatePresence>
              {expandedId === occupant.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="border-t border-gray-100 p-4">
                    {/* Tab Navigation */}
                    <div className="flex gap-2 mb-4 border-b border-gray-100 pb-2">
                      <button
                        onClick={() =>
                          handleTabChange(occupant.id, "Information")
                        }
                        className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded transition-colors duration-200 ${
                          activeTab[occupant.id] === "Information"
                            ? "bg-[#363062] text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        <IoInformationCircleOutline className="w-3.5 h-3.5 mr-1" />
                        Information
                      </button>
                      <button
                        onClick={() =>
                          handleTabChange(occupant.id, "Documents")
                        }
                        className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded transition-colors duration-200 ${
                          activeTab[occupant.id] === "Documents"
                            ? "bg-[#363062] text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        <IoDocumentTextOutline className="w-3.5 h-3.5 mr-1" />
                        Documents
                      </button>
                    </div>

                    {/* Tab Content */}
                    {activeTab[occupant.id] === "Information" && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                        <div className="space-y-3">
                          {renderProfile(occupant)}
                          {/* Personal Information */}
                          <div className="rounded-md border border-gray-100 overflow-hidden">
                            <div className="bg-[#363062]/5 px-3 py-1.5 flex items-center">
                              <FaRegUser className="w-3 h-3 text-[#363062] mr-1.5" />
                              <span className="text-xs font-medium text-[#363062]">
                                Personal Details
                              </span>
                            </div>
                            <div className="p-3 space-y-2">
                              {editingOccupantId === occupant.id ? (
                                <>
                                  {renderEditableField(
                                    "First Name",
                                    "firstName",
                                    editFormData.firstName
                                  )}
                                  {renderEditableField(
                                    "Middle Name",
                                    "middleName",
                                    editFormData.middleName
                                  )}
                                  {renderEditableField(
                                    "Last Name",
                                    "lastName",
                                    editFormData.lastName
                                  )}
                                  {renderEditableField(
                                    "Gender",
                                    "gender",
                                    editFormData.gender,
                                    <FaVenusMars />
                                  )}
                                  {renderEditableField(
                                    "Date of Birth",
                                    "dateOfBirth",
                                    editFormData.dateOfBirth,
                                    <FaBirthdayCake />
                                  )}
                                  {renderEditableField(
                                    "Relationship",
                                    "relationship",
                                    editFormData.relationship,
                                    <FaUserTie />
                                  )}
                                </>
                              ) : (
                                <>
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-500 flex items-center">
                                      <FaVenusMars className="w-2.5 h-2.5 mr-1 text-gray-400" />
                                      Gender
                                    </span>
                                    <span className="text-xs font-medium">
                                      {occupant.gender || "Not specified"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-500 flex items-center">
                                      <FaBirthdayCake className="w-2.5 h-2.5 mr-1 text-gray-400" />
                                      Date of Birth
                                    </span>
                                    <span className="text-xs font-medium">
                                      {occupant.dateOfBirth || "Not specified"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-500 flex items-center">
                                      <FaUserTie className="w-2.5 h-2.5 mr-1 text-gray-400" />
                                      Relationship
                                    </span>
                                    <span className="text-xs font-medium">
                                      {occupant.relationship || "Not specified"}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Contact Information */}
                          <div className="rounded-md border border-gray-100 overflow-hidden">
                            <div className="bg-[#363062]/5 px-3 py-1.5 flex items-center">
                              <FaPhoneAlt className="w-3 h-3 text-[#363062] mr-1.5" />
                              <span className="text-xs font-medium text-[#363062]">
                                Contact Details
                              </span>
                            </div>
                            <div className="p-3 space-y-2">
                              {editingOccupantId === occupant.id ? (
                                <>
                                  {renderEditableField(
                                    "Phone",
                                    "phoneNumber",
                                    editFormData.phoneNumber
                                  )}
                                  {renderEditableField(
                                    "Email",
                                    "email",
                                    editFormData.email
                                  )}
                                </>
                              ) : (
                                <>
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-500">
                                      Phone
                                    </span>
                                    <span className="text-xs font-medium">
                                      {occupant.phoneNumber || "Not specified"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-500">
                                      Email
                                    </span>
                                    <span className="text-xs font-medium">
                                      {occupant.email || "Not specified"}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {/* Identification */}
                          <div className="rounded-md border border-gray-100 overflow-hidden">
                            <div className="bg-[#363062]/5 px-3 py-1.5 flex items-center">
                              <FaIdCard className="w-3 h-3 text-[#363062] mr-1.5" />
                              <span className="text-xs font-medium text-[#363062]">
                                Identification
                              </span>
                            </div>
                            <div className="p-3 space-y-2">
                              {editingOccupantId === occupant.id ? (
                                <>
                                  {renderEditableField(
                                    "ID Type",
                                    "idType",
                                    editFormData.idType
                                  )}
                                  {renderEditableField(
                                    "ID Number",
                                    "idNumber",
                                    editFormData.idNumber
                                  )}
                                </>
                              ) : (
                                <>
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-500">
                                      ID Type
                                    </span>
                                    <span className="text-xs font-medium">
                                      {occupant.idType || "Not specified"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-500">
                                      ID Number
                                    </span>
                                    <span className="text-xs font-medium">
                                      {occupant.idNumber || "Not specified"}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Additional Info */}
                          <div className="rounded-md border border-gray-100 overflow-hidden">
                            <div className="bg-[#363062]/5 px-3 py-1.5 flex items-center">
                              <FaCalendarAlt className="w-3 h-3 text-[#363062] mr-1.5" />
                              <span className="text-xs font-medium text-[#363062]">
                                Additional Information
                              </span>
                            </div>
                            <div className="p-3 space-y-2">
                              {editingOccupantId === occupant.id ? (
                                <>
                                  {renderEditableField(
                                    "Status",
                                    "status",
                                    editFormData.status
                                  )}
                                  {renderEditableField(
                                    "Date Added",
                                    "dateAdded",
                                    editFormData.dateAdded
                                  )}
                                </>
                              ) : (
                                <>
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-500">
                                      Status
                                    </span>
                                    <span className="text-xs font-medium bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                      {occupant.status || "Active"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-500">
                                      Date Added
                                    </span>
                                    <span className="text-xs font-medium">
                                      {occupant.dateAdded || "Not specified"}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab[occupant.id] === "Documents" && (
                      <div className="p-4 bg-gray-50 rounded-md">
                        {/* Two-column layout for documents */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Left Column - Document Uploader */}
                          <div className="bg-white rounded-lg border border-gray-100 p-3 shadow-sm">
                            <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
                              <div className="flex items-center gap-1.5">
                                <IoCloudUploadOutline className="w-3.5 h-3.5 text-[#363062]" />
                                <h3 className="text-xs font-medium text-[#363062]">
                                  Upload Document
                                </h3>
                              </div>
                            </div>

                            <div className="space-y-3">
                              <div>
                                <label className="block text-[10px] font-medium text-gray-700 mb-1">
                                  Document Type*
                                </label>
                                <select
                                  value={documentType}
                                  onChange={(e) =>
                                    setDocumentType(e.target.value)
                                  }
                                  className="w-full text-xs border border-gray-200 rounded-md p-1.5 focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062]"
                                  required
                                >
                                  <option value="">Select a type</option>
                                  <option value="Identification">
                                    Identification
                                  </option>
                                  <option value="Passport">Passport</option>
                                  <option value="Driver's License">
                                    Driver's License
                                  </option>
                                  <option value="Birth Certificate">
                                    Birth Certificate
                                  </option>
                                  <option value="Visa/Immigration">
                                    Visa/Immigration
                                  </option>
                                  <option value="Employment">Employment</option>
                                  <option value="Medical">Medical</option>
                                  <option value="Other">Other</option>
                                </select>
                              </div>

                              <div>
                                <label className="block text-[10px] font-medium text-gray-700 mb-1">
                                  Document Name*
                                </label>
                                <input
                                  type="text"
                                  value={documentName}
                                  onChange={(e) =>
                                    setDocumentName(e.target.value)
                                  }
                                  placeholder="Enter document name"
                                  className="w-full text-xs border border-gray-200 rounded-md p-1.5 focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062]"
                                  required
                                />
                              </div>

                              <div>
                                <label className="block text-[10px] font-medium text-gray-700 mb-1">
                                  Description (Optional)
                                </label>
                                <textarea
                                  value={documentDescription}
                                  onChange={(e) =>
                                    setDocumentDescription(e.target.value)
                                  }
                                  placeholder="Add a brief description"
                                  rows="2"
                                  className="w-full text-xs border border-gray-200 rounded-md p-1.5 focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062]"
                                />
                              </div>

                              <div>
                                <label className="block text-[10px] font-medium text-gray-700 mb-1">
                                  Select File*
                                </label>
                                <input
                                  type="file"
                                  ref={fileInputRef}
                                  className="hidden"
                                  onChange={(e) =>
                                    handleDocumentUpload(e, occupant.id)
                                  }
                                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                />
                                <button
                                  onClick={() => fileInputRef.current.click()}
                                  disabled={
                                    uploadingDoc ||
                                    !documentType ||
                                    !documentName
                                  }
                                  className={`w-full text-xs px-3 py-1.5 rounded-md flex items-center justify-center ${
                                    uploadingDoc ||
                                    !documentType ||
                                    !documentName
                                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                      : "bg-[#363062] text-white hover:bg-[#363062]/90"
                                  }`}
                                >
                                  <IoCloudUploadOutline className="w-3.5 h-3.5 mr-1.5" />
                                  {uploadingDoc
                                    ? "Uploading..."
                                    : "Upload Document"}
                                </button>
                              </div>

                              {uploadingDoc && (
                                <div className="mt-2">
                                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                                    <div
                                      className="bg-[#8FCD00] h-1.5 rounded-full"
                                      style={{ width: `${uploadProgress}%` }}
                                    ></div>
                                  </div>
                                  <p className="text-right text-[10px] text-gray-500 mt-1">
                                    {uploadProgress}%
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Right Column - Document List */}
                          <div className="bg-white rounded-lg border border-gray-100 p-3 shadow-sm">
                            <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
                              <div className="flex items-center gap-1.5">
                                <IoDocumentTextOutline className="w-3.5 h-3.5 text-[#363062]" />
                                <h3 className="text-xs font-medium text-[#363062]">
                                  Occupant Documents
                                </h3>
                              </div>
                            </div>

                            {occupant.documents &&
                            occupant.documents.length > 0 ? (
                              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                {occupant.documents.map((doc) => (
                                  <div
                                    key={doc.id}
                                    className="bg-white border border-gray-200 rounded-md p-3 flex justify-between items-center hover:bg-gray-50"
                                  >
                                    <div className="flex items-center gap-3">
                                      {getDocumentIcon(doc.fileType)}
                                      <div>
                                        <h4 className="text-xs font-medium text-gray-800">
                                          {doc.name}
                                        </h4>
                                        <p className="text-[10px] text-gray-500">
                                          {doc.type} •{" "}
                                          {new Date(
                                            doc.uploadDate
                                          ).toLocaleDateString()}
                                        </p>
                                        {doc.description && (
                                          <p className="text-[10px] text-gray-600 mt-1">
                                            {doc.description}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <a
                                        href={doc.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[10px] bg-blue-50 text-blue-600 px-2 py-1 rounded flex items-center"
                                      >
                                        <FaFileDownload className="w-2.5 h-2.5 mr-1" />
                                        View
                                      </a>
                                      <button
                                        onClick={() =>
                                          handleDeleteDocument(
                                            occupant.id,
                                            doc.id
                                          )
                                        }
                                        disabled={isProcessing}
                                        className="text-[10px] bg-red-50 text-red-600 px-2 py-1 rounded flex items-center"
                                      >
                                        <IoTrashOutline className="w-2.5 h-2.5 mr-1" />
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center py-6 bg-gray-50 rounded-md h-full">
                                <div className="w-12 h-12 mb-3 rounded-full bg-[#363062]/5 flex items-center justify-center">
                                  <IoDocumentTextOutline className="w-5 h-5 text-[#363062]/30" />
                                </div>
                                <p className="text-xs text-gray-500 mb-1">
                                  No documents available
                                </p>
                                <p className="text-[10px] text-gray-400 mb-2">
                                  Upload identification documents and other
                                  files
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex justify-end mt-4 border-t border-gray-100 pt-3">
                      {editingOccupantId === occupant.id ? (
                        <>
                          <button
                            onClick={handleCancelEdit}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors duration-200 mr-2"
                            disabled={isProcessing}
                          >
                            <IoCloseCircleOutline className="w-3.5 h-3.5 mr-1.5" />
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveEdit}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-[#8FCD00] rounded-md hover:bg-[#8FCD00]/90 transition-colors duration-200"
                            disabled={isProcessing}
                          >
                            {isProcessing ? (
                              <div className="animate-spin rounded-full h-3.5 w-3.5 border-t-2 border-b-2 border-white mr-1.5"></div>
                            ) : (
                              <IoSaveOutline className="w-3.5 h-3.5 mr-1.5" />
                            )}
                            Save
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditOccupant(occupant)}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-[#8FCD00] rounded-md hover:bg-[#8FCD00]/90 transition-colors duration-200 mr-2"
                          >
                            <IoPencilOutline className="w-3.5 h-3.5 mr-1.5" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleViewOccupant(occupant)}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-[#363062] rounded-md hover:bg-[#363062]/90 transition-colors duration-200"
                          >
                            <IoEyeOutline className="w-3.5 h-3.5 mr-1.5" />
                            View Full Details
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}

        {filteredOccupants.length === 0 && (
          <div className="text-center py-8 bg-white rounded-lg shadow-sm border border-gray-100">
            {searchTerm ? (
              <>
                <FaRegUser className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <h3 className="text-sm font-medium text-gray-900 mb-1">
                  No matching occupants found
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Try adjusting your search terms
                </p>
              </>
            ) : (
              <>
                <FaRegUser className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <h3 className="text-sm font-medium text-gray-900 mb-1">
                  No Occupants Added
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Start by adding occupants to this tenant
                </p>
                <button
                  onClick={handleAddOccupant}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-[#8FCD00] rounded-md hover:bg-[#8FCD00]/90 transition-colors duration-200"
                >
                  <IoPersonAddOutline className="w-3.5 h-3.5 mr-1.5" />
                  Add First Occupant
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {addMode && (
        <Modal>
          <div className="bg-white rounded-lg shadow-lg p-4 max-w-md mx-auto">
            <TenantOccupantForm
              handleAddMode={handleAddOccupant}
              tenant={tenant}
            />
          </div>
        </Modal>
      )}

      {viewMode && (
        <PageModal>
          <TenantOccupantDetails
            tenantId={id}
            occupantId={selectedOccupant.id}
            handleViewMode={handleViewOccupant}
            tenant={tenant}
          />
        </PageModal>
      )}
    </div>
  );
}
